import { Package } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CaseCargo, CaseDetail } from '@/types/domain'

/** The figures the real case read serves beside the description: decimal
 *  strings, each absent when nobody stated it (`casefile.CaseDTO`). */
export type TypedCargoFigures = Pick<
  CaseDetail,
  | 'declaredWeightKg'
  | 'measuredWeightKg'
  | 'declaredVolumeM3'
  | 'measuredVolumeM3'
  | 'weightDiffers'
  | 'volumeDiffers'
>

/** A stored quantity at its own precision. The server's scale is three places
 *  (`22000.000`); printing all of them is noise, and rounding to a fixed count
 *  would misstate `1250.5`. The string is trimmed, then handed to
 *  `formatNumber` for the grouping — no arithmetic happens on it. */
function formatQuantity(value: string): string {
  const trimmed = value.includes('.') ? value.replace(/\.?0+$/, '') : value
  const digits = trimmed.includes('.') ? trimmed.length - trimmed.indexOf('.') - 1 : 0
  return formatNumber(trimmed, digits)
}

/** The declared load, then what the scales said about it. The declared total
 *  and the control weigh sit in the same column as the lines they contest, so
 *  a +530 kg discrepancy is read against the declaration that produced it. */
export function CargoCard({
  cargo,
  description,
  figures,
}: {
  cargo?: CaseCargo
  /** The cargo as the desk typed it (`cargoDescription`), printed when the
   *  structured `cargo` is not served — which, outside the showcase fixture,
   *  is every case today. */
  description?: string
  /** The weight and volume that go with `description`, in the same case. */
  figures?: TypedCargoFigures
}) {
  const { t } = useTranslation()

  /* Weight and volume as one right-hand figure, whichever of the two exist —
     a line measured only by weight must not render a dangling separator. */
  const measures = (weightKg?: string, volumeCbm?: string) =>
    [
      weightKg ? `${formatNumber(weightKg)} ${t('cases.overview.kg')}` : null,
      volumeCbm ? `${formatNumber(volumeCbm, 1)} ${t('cases.overview.cbm')}` : null,
    ]
      .filter(Boolean)
      .join(' · ')

  /* A case with no structured cargo prints what the desk typed — the
     description and the declared and measured figures beside it — and one with
     nothing at all keeps its card in the overview's grid
     (S-11 revision) and says so, rather than leaving a hole the next card
     slides into. */
  if (!cargo) {
    const text = description?.trim()
    const typedMeasures = (weightKg?: string, volumeM3?: string) =>
      [
        weightKg ? `${formatQuantity(weightKg)} ${t('cases.overview.kg')}` : null,
        volumeM3 ? `${formatQuantity(volumeM3)} ${t('cases.overview.cbm')}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    const declared = typedMeasures(figures?.declaredWeightKg, figures?.declaredVolumeM3)
    const measuredText = typedMeasures(figures?.measuredWeightKg, figures?.measuredVolumeM3)
    const differs = Boolean(figures?.weightDiffers || figures?.volumeDiffers)

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package size={15} className="text-text-muted" aria-hidden />
            {t('cases.overview.cargo')}
          </CardTitle>
        </CardHeader>
        <CardBody className="pt-3 pb-4">
          {text ? (
            <p className="text-text-body text-[13px] whitespace-pre-line">{text}</p>
          ) : !declared && !measuredText ? (
            <p className="text-text-muted text-[13px] font-light">
              {t('cases.overview.cargoEmpty')}
            </p>
          ) : null}
          {declared || measuredText ? (
            <ul className={cn('flex flex-col', text ? 'mt-2' : null)}>
              {declared ? (
                <li className="border-line-hair flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-2 last:border-b-0">
                  <span className="text-text-body text-[13px]">
                    {t('cases.overview.declaredTotal')}
                  </span>
                  <span className="flex-1" />
                  <span className="asa-num text-text-heading text-[12.5px] font-semibold whitespace-nowrap">
                    {declared}
                  </span>
                </li>
              ) : null}
              {measuredText ? (
                <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                  <span className="text-text-body text-[13px]">
                    {t('cases.overview.measuredTotal')}
                  </span>
                  {/* The word restates what the tone would say alone. */}
                  {differs ? (
                    <Tag tone="attention">{t('cases.overview.differsFromDeclared')}</Tag>
                  ) : null}
                  <span className="flex-1" />
                  <span
                    className={cn(
                      'asa-num text-[12.5px] font-semibold whitespace-nowrap',
                      differs ? 'text-attention' : 'text-text-heading',
                    )}
                  >
                    {measuredText}
                  </span>
                </li>
              ) : null}
            </ul>
          ) : null}
        </CardBody>
      </Card>
    )
  }

  const measured = cargo.measured
  /* The sign decides the tone, the NUMBER is served (AD-9): the subtraction
     happened where the decimals are exact. Over the declaration is what the
     border argues about; under it is a curiosity. */
  const over = measured ? Number(measured.deltaKg) > 0 : false

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package size={15} className="text-text-muted" aria-hidden />
          {t('cases.overview.cargo')}
        </CardTitle>
      </CardHeader>
      <CardBody className="pt-1 pb-3">
        <ul className="flex flex-col">
          {cargo.lines.map((line) => (
            <li
              key={line.id}
              className="border-line-hair flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-2.5"
            >
              <span className="text-text-heading flex items-center gap-2 text-[13px]">
                {line.description}
                {/* Dangerous goods are flagged where the cargo is named — the
                    IMO class is why this line needs an MSDS on file and a
                    different truck at the border. */}
                {line.imoClass ? (
                  <Tag tone="attention">{t('cases.overview.imo', { class: line.imoClass })}</Tag>
                ) : null}
              </span>
              <span className="flex-1" />
              <span className="asa-num text-text-body text-[12.5px] whitespace-nowrap">
                {measures(line.weightKg, line.volumeCbm)}
              </span>
            </li>
          ))}

          <li className="border-line-hair flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-2.5 last:border-b-0">
            <span className="text-text-body text-[13px]">{t('cases.overview.declaredTotal')}</span>
            <span className="flex-1" />
            <span className="asa-num text-text-heading text-[12.5px] font-semibold whitespace-nowrap">
              {measures(cargo.declaredWeightKg, cargo.declaredVolumeCbm)}
            </span>
          </li>

          {measured ? (
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
              <span className="text-text-body text-[13px]">
                {t('cases.overview.measuredAt', {
                  location: measured.location,
                })}
              </span>
              <span className="flex-1" />
              <span
                className={cn(
                  'asa-num text-[12.5px] font-semibold whitespace-nowrap',
                  over ? 'text-attention' : 'text-text-heading',
                )}
              >
                {formatNumber(measured.weightKg)} {t('cases.overview.kg')} ({over ? '+' : ''}
                {formatNumber(measured.deltaKg)})
              </span>
            </li>
          ) : null}
        </ul>
      </CardBody>
    </Card>
  )
}
